import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import { specValue } from '../../domain/cars/carFigures';
import { briefCars } from '../../domain/compare/carBriefing';
import { formatSpecNumber } from '../../domain/cars/carProperties';

/**
 * The two cars, before the lights (SPEC-008 PR-8.6).
 *
 * What each one brings and what it is carrying, on this road, against this
 * opponent. It is the difference between watching two dots race and watching an
 * argument settle: a reader who has been told that one of them cannot put its
 * power down knows what they are looking for when it spins.
 *
 * Every line is composed from a figure the catalogue already carries, through
 * one message key per trait — `carBriefing.js` explains why none of it is
 * authored per car and why the traits are relative to the pairing rather than
 * properties of a record.
 *
 * It sits ABOVE the strip and stays through the run, because it is the context
 * for what the reader is watching rather than a thing they read once. It goes at
 * the LINE, where the verdict opens in its place — so the one layout shift it
 * causes lands at the moment attention has moved to the result anyway, rather
 * than at the moment the reader taps Run and looks at the cars.
 *
 * An empty side is left empty rather than filled. Two cars of the same weight,
 * the same layout and within twenty horsepower per tonne have nothing to say to
 * each other before the lights, and a sentence invented for that case would be
 * the panel padding itself.
 */

/** The one figure that explains most of the others, shown for both regardless. */
const HEADLINE = 'powerToWeight';

function Trait({ entry, kind }) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    // Every trait's substitutions are figures, so they are formatted here rather
    // than in the domain — which is the same division `specFigure` keeps.
    const values = Object.fromEntries(
        Object.entries(entry.values).map(([name, value]) => [
            name,
            typeof value === 'number' ? formatSpecNumber(value, { languageCode }) : value,
        ]),
    );

    return (
        <li className={`race-briefing__trait race-briefing__trait--${kind}`}>
            {t(`compare.trait.${entry.key}`, values)}
        </li>
    );
}

function BriefingSide({ brief, car }) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    const headline = specValue(car, HEADLINE);

    return (
        <div className="race-briefing__side">
            <p className="race-briefing__car">{carName(car)}</p>

            <p className="race-briefing__headline">
                <span className="race-briefing__figure">
                    {formatSpecNumber(headline, { languageCode })}
                </span>
                <span className="race-briefing__unit">{t('cars.spec.powerToWeight.unit')}</span>
            </p>

            {/* Two lists rather than one with a marker column: a strength and a
                flaw are different kinds of claim, and a screen reader running
                down a single list would hear six statements with no indication
                which two of them are the problems. */}
            {brief.strengths.length > 0 ? (
                <ul className="race-briefing__traits" aria-label={t('compare.brief.strengths')}>
                    {brief.strengths.map((entry) => (
                        <Trait key={entry.key} entry={entry} kind="strength" />
                    ))}
                </ul>
            ) : null}

            {brief.flaws.length > 0 ? (
                <ul className="race-briefing__traits" aria-label={t('compare.brief.flaws')}>
                    {brief.flaws.map((entry) => (
                        <Trait key={entry.key} entry={entry} kind="flaw" />
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

export default function RaceBriefing({ cars, conditions }) {
    const t = useTranslate();
    const briefs = briefCars(cars, conditions);

    if (briefs.length === 0) {
        return null;
    }

    return (
        <section className="race-briefing" aria-label={t('compare.brief.title')}>
            {briefs.map((brief, index) => (
                <BriefingSide key={brief.carId + String(index)} brief={brief} car={cars[index]} />
            ))}
        </section>
    );
}
